import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, ArrowLeft, Pencil, Trash2, Users, MapPin, 
  LayoutGrid, ChefHat, CheckCircle, Clock, Sparkles,
  QrCode, RefreshCw, Copy, ExternalLink, Wifi, Play
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { useUser } from '@/utils/userHelper';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function RestaurantTables({ user: propUser, isTab, tabId, tabData }) {
  const user = useUser(propUser);
  // Support both route params and tabData
  const { restaurantId: routeRestaurantId } = useParams();
  const restaurantId = tabData?.restaurantId || routeRestaurantId;
  const navigate = useNavigate();
  
  const [restaurant, setRestaurant] = useState(tabData?.restaurant || null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [generatingSession, setGeneratingSession] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [formData, setFormData] = useState({
    table_number: '',
    capacity: '4',
    location: '',
    rfid_tag: ''
  });

  const canManage = ['super_admin', 'hotel_admin', 'manager'].includes(user?.role);

  const locations = ['Indoor', 'Outdoor', 'Poolside', 'Rooftop', 'Private Dining', 'Bar Area', 'Terrace', 'Garden'];

  const statusConfig = {
    created: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: LayoutGrid, label: 'Created' },
    available: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Available' },
    occupied: { color: 'bg-red-100 text-red-700 border-red-200', icon: Users, label: 'Occupied' },
    reserved: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'Reserved' },
    cleaning: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Sparkles, label: 'Cleaning' }
  };

  useEffect(() => {
    loadData();
  }, [restaurantId]);

  const loadData = async () => {
    try {
      const [restaurantRes, tablesRes] = await Promise.all([
        api.get(`/restaurants/${restaurantId}`),
        api.get(`/tables?restaurant_id=${restaurantId}`)
      ]);
      setRestaurant(restaurantRes.data);
      setTables(tablesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      if (error.response?.status === 404) {
        navigate('/admin/restaurants');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTable(null);
    setFormData({ table_number: '', capacity: '4', location: '', rfid_tag: '' });
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setFormData({
      table_number: table.table_number,
      capacity: table.capacity?.toString() || '4',
      location: table.location || '',
      rfid_tag: table.rfid_tag || ''
    });
    setDialogOpen(true);
  };

  const openQRDialog = (table) => {
    setSelectedTable(table);
    setCurrentSession(null);
    setQrDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.table_number.trim()) {
      toast.error('Table number is required');
      return;
    }

    const payload = {
      restaurant_id: restaurantId,
      table_number: formData.table_number.trim(),
      capacity: parseInt(formData.capacity) || 4,
      location: formData.location || null,
      rfid_tag: formData.rfid_tag || null
    };

    try {
      if (editingTable) {
        await api.put(`/tables/${editingTable.id}`, payload);
        toast.success('Table updated');
      } else {
        await api.post('/tables', payload);
        toast.success('Table created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save table'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this table?')) return;
    try {
      await api.delete(`/tables/${id}`);
      toast.success('Table deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete table');
    }
  };

  const handleStatusChange = async (tableId, newStatus) => {
    try {
      await api.patch(`/tables/${tableId}/status?status=${newStatus}`);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const generateSession = async (tableId) => {
    setGeneratingSession(true);
    try {
      const response = await api.post(`/tables/${tableId}/generate-session`);
      setCurrentSession(response.data);
      toast.success('Session generated! Customer can now scan to order.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate session. Make sure self-ordering is enabled in restaurant settings.'));
    } finally {
      setGeneratingSession(false);
    }
  };

  const regenerateCode = async (tableId) => {
    try {
      const response = await api.post(`/tables/${tableId}/regenerate-code`);
      toast.success('QR code regenerated');
      loadData();
      if (selectedTable?.id === tableId) {
        setSelectedTable(prev => ({
          ...prev,
          unique_code: response.data.unique_code,
          qr_code_url: response.data.qr_code_url
        }));
      }
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Group tables by location
  const tablesByLocation = tables.reduce((acc, table) => {
    const loc = table.location || 'Unassigned';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(table);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="tables-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading tables...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="restaurant-tables">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/restaurants')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">{restaurant?.name}</h1>
            <Badge variant="secondary">
              <ChefHat className="h-3 w-3 mr-1" />
              {restaurant?.cuisine_type || 'Restaurant'}
            </Badge>
            {restaurant?.self_order_enabled && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <QrCode className="h-3 w-3 mr-1" />
                QR Self-Order
              </Badge>
            )}
          </div>
          <p className="text-gray-500">Table Management</p>
        </div>
        {canManage && (
          <Button onClick={openAddDialog} className="bg-primary hover:bg-primary/90" data-testid="add-table-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Table
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const count = tables.filter(t => t.status === status).length;
          const Icon = config.icon;
          return (
            <Card key={status} className={`border ${config.color.split(' ')[2]}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg ${config.color.split(' ')[0]} flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${config.color.split(' ')[1]}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-gray-500">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tables by Location */}
      {tables.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <LayoutGrid className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Tables Yet</h3>
            <p className="text-gray-400 mb-4">Add tables to this restaurant to start taking orders</p>
            {canManage && (
              <Button onClick={openAddDialog} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Table
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(tablesByLocation).map(([location, locationTables]) => (
            <div key={location}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-gray-400" />
                <h3 className="font-semibold text-gray-700">{location}</h3>
                <Badge variant="outline">{locationTables.length} tables</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {locationTables.map((table) => {
                  const statusInfo = statusConfig[table.status] || statusConfig.available;
                  const Icon = statusInfo.icon;
                  return (
                    <Card 
                      key={table.id} 
                      className={`relative overflow-hidden border-2 ${statusInfo.color.split(' ')[2]} hover:shadow-md transition-shadow`}
                      data-testid={`table-${table.id}`}
                    >
                      <CardContent className="p-4">
                        <div className={`absolute top-0 left-0 right-0 h-1 ${statusInfo.color.split(' ')[0].replace('100', '500')}`} />
                        <div className="text-center mb-2">
                          <p className="text-xl font-bold text-gray-800">Table {table.table_number}</p>
                          <div className="flex items-center justify-center gap-1 text-gray-500 text-sm">
                            <Users className="h-3 w-3" />
                            <span>{table.capacity} seats</span>
                          </div>
                        </div>
                        
                        {/* Unique Code */}
                        {table.unique_code && (
                          <div className="text-xs text-center text-gray-400 font-mono bg-gray-50 rounded px-2 py-1 mb-2">
                            ID: {table.unique_code}
                          </div>
                        )}
                        
                        {/* RFID Indicator */}
                        {table.rfid_tag && (
                          <div className="flex items-center justify-center gap-1 text-xs text-blue-600 mb-2">
                            <Wifi className="h-3 w-3" />
                            NFC
                          </div>
                        )}
                        
                        <Badge className={`${statusInfo.color} text-xs w-full justify-center`}>
                          <Icon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                        
                        {canManage && (
                          <div className="mt-3 space-y-2">
                            <Select
                              value={table.status}
                              onValueChange={(value) => handleStatusChange(table.id, value)}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusConfig).map(([s, c]) => (
                                  <SelectItem key={s} value={s}>{c.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="flex-1 h-8"
                                onClick={() => openQRDialog(table)}
                              >
                                <QrCode className="h-3 w-3 mr-1" />
                                QR
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(table)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(table.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Table Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTable ? 'Edit Table' : 'Add New Table'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="table_number">Table Number/Name *</Label>
              <Input
                id="table_number"
                value={formData.table_number}
                onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                placeholder="e.g., 1, A1, VIP-1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity">Seating Capacity</Label>
              <Select
                value={formData.capacity}
                onValueChange={(v) => setFormData({ ...formData, capacity: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2, 4, 6, 8, 10, 12].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} seats</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={formData.location}
                onValueChange={(v) => setFormData({ ...formData, location: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rfid_tag">RFID/NFC Tag ID (Optional)</Label>
              <Input
                id="rfid_tag"
                value={formData.rfid_tag}
                onChange={(e) => setFormData({ ...formData, rfid_tag: e.target.value })}
                placeholder="For tap-to-order functionality"
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingTable ? 'Update' : 'Add'} Table
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Table {selectedTable?.table_number} - QR Self-Order
            </DialogTitle>
            <DialogDescription>
              Generate QR codes for customer self-ordering
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="session" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="session">Session QR</TabsTrigger>
              <TabsTrigger value="static">Static QR</TabsTrigger>
            </TabsList>
            
            {/* Session QR Tab - Default and Recommended */}
            <TabsContent value="session" className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  One-time session QR. Expires after configured time or when order is placed.
                </p>
                
                {currentSession ? (
                  <>
                    <div className="inline-block bg-white p-4 rounded-lg shadow-sm">
                      <QRCode 
                        value={`${API_BASE_URL}${currentSession.order_url}`}
                        size={180}
                      />
                    </div>
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-mono text-gray-500">
                        Session: {currentSession.session?.session_code}
                      </p>
                      <div className="flex items-center justify-center gap-1 text-xs text-orange-600">
                        <Clock className="h-3 w-3" />
                        Expires: {new Date(currentSession.session?.expires_at).toLocaleTimeString()}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-8">
                    <QrCode className="h-16 w-16 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No active session</p>
                    <p className="text-xs text-gray-400">Click button below to generate</p>
                  </div>
                )}
              </div>
              
              <Button 
                className="w-full bg-primary"
                onClick={() => generateSession(selectedTable?.id)}
                disabled={generatingSession}
              >
                {generatingSession ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {currentSession ? 'Generate New Session' : 'Generate QR Session'}
                  </>
                )}
              </Button>
              
              {currentSession && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => copyToClipboard(`${API_BASE_URL}${currentSession.order_url}`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy URL
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`${API_BASE_URL}${currentSession.order_url}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                ✓ Session QR is secure - expires after one order or timeout.
              </p>
            </TabsContent>
            
            {/* Static QR Tab */}
            <TabsContent value="static" className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  Permanent QR code. Good for printed materials but less secure.
                </p>
                <div className="inline-block bg-white p-4 rounded-lg shadow-sm">
                  {selectedTable?.unique_code ? (
                    <QRCode 
                      value={`${API_BASE_URL}/self-order/${selectedTable.unique_code}`}
                      size={180}
                    />
                  ) : (
                    <div className="h-[180px] w-[180px] flex items-center justify-center text-gray-400">
                      No code available
                    </div>
                  )}
                </div>
                <p className="text-xs mt-3 font-mono text-gray-500">
                  Code: {selectedTable?.unique_code || 'N/A'}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => copyToClipboard(`${API_BASE_URL}/self-order/${selectedTable?.unique_code}`)}
                  disabled={!selectedTable?.unique_code}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => regenerateCode(selectedTable?.id)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
              </div>
              
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Static QR requires self-ordering to be enabled in restaurant settings.
              </p>
            </TabsContent>
          </Tabs>
          
          {/* RFID Section */}
          {selectedTable?.rfid_tag && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Wifi className="h-4 w-4" />
                <span>RFID/NFC Tag: <code className="bg-gray-100 px-1 rounded">{selectedTable.rfid_tag}</code></span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
